/**
 * Maps a Parsed1040Return (the extractor-neutral intermediate
 * representation) into the wizard's WizardInput shape.
 *
 * This is the seam between extractors and the UI: extractors evolve
 * independently, the wizard only ever consumes the output of this
 * function. Adding a new extractor (text-positional, IRS fillable,
 * etc.) doesn't touch the wizard at all.
 *
 * Pure function. No side effects. Returns a partial WizardInput plus
 * a flat list of "extracted items" the review dialog renders, plus
 * the warnings (extractor warnings + cross-validation warnings, both).
 */
import type { TaxScenarioInput } from '@/tax/types';
import type {
  Parsed1040Return,
  ExtractedField,
  ExtractionWarning,
} from './parse1040Types';

export interface MappedWizardImport {
  fields: Partial<TaxScenarioInput>;
  /** Flat list for the review dialog table — one row per detected field. */
  extracted: Array<{ label: string; line: string; value: number | string }>;
  /** Human-readable warning strings for the review dialog alerts. */
  warnings: string[];
  /** Things the advisor must fix before the numbers are right — shown at
   *  the top of the review dialog. */
  /** Joint return where a business owner's own W-2 wages can't be read (no
   *  Schedule SE in the PDF): the combined wage total, which stays on the
   *  taxpayer; the import screen says to split it by hand. Null otherwise. */
  wageSplitTotal: number | null;
  /** Structured warnings (preserved for future UI features like
   *  per-field severity badges). */
  structuredWarnings: ExtractionWarning[];
}

/** Helper: pull the value out of an ExtractedField, or undefined. */
function v<T>(f: ExtractedField<T> | undefined): T | undefined {
  return f?.value;
}

/** Two-letter state abbreviation → full name expected by the engine schema. */
const US_STATE_ABBR_TO_NAME: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Helper: assign a field if the source is present, and record an
 *  "extracted" row for the review UI. */
function assign<K extends keyof TaxScenarioInput>(
  fields: Partial<TaxScenarioInput>,
  extracted: Array<{ label: string; line: string; value: number | string }>,
  key: K,
  source: ExtractedField<TaxScenarioInput[K] & (number | string | boolean)> | undefined,
  label: string,
): void {
  if (!source) return;
  // Engine treats numbers/strings/booleans by index assignment; we cast to satisfy TS.
  (fields as Record<string, unknown>)[key] = source.value;
  extracted.push({
    label,
    line: `${source.sourceForm}-${source.sourceLineNumber}`,
    value: source.value as number | string,
  });
}

export function mapToWizardInput(parsed: Parsed1040Return): MappedWizardImport {
  const fields: Partial<TaxScenarioInput> = {};
  const extracted: Array<{ label: string; line: string; value: number | string }> = [];

  // ── Header / personal ────────────────────────────────────────────
  // The return's year is never imported: every scenario is a 2026 projection.
  if (parsed.filingStatus) {
    fields.filingStatus = parsed.filingStatus.value;
    extracted.push({ label: 'Filing Status', line: 'header', value: parsed.filingStatus.value });
  }

  // Compose client name from parts.
  const clientName = formatClientName(
    v(parsed.filingStatus) === 'MFJ',
    v(parsed.taxpayerFirstName),
    v(parsed.taxpayerLastName),
    v(parsed.spouseFirstName),
    v(parsed.spouseLastName),
  );
  if (clientName) {
    fields.name = clientName;
    extracted.push({ label: 'Client Name', line: 'header', value: clientName });
  }

  if (parsed.addressState) {
    // Engine schema accepts only the full state name ("Texas", "California"),
    // but the parser pulls a 2-letter abbreviation from the address. Convert
    // here so Save & Calculate doesn't fail Zod validation.
    const fullName = US_STATE_ABBR_TO_NAME[parsed.addressState.value];
    if (fullName) {
      fields.state = fullName;
      extracted.push({ label: 'State', line: 'header', value: fullName });
    }
  }

  // Age 65+ implies 55+ in the wizard's age-tier logic.
  if (parsed.taxpayer65Plus?.value) {
    fields.tp65Plus = true;
    fields.tp55Plus = true;
    extracted.push({ label: 'Taxpayer 65+', line: 'header', value: 'Yes' });
  }
  if (parsed.spouse65Plus?.value) {
    fields.sp65Plus = true;
    fields.sp55Plus = true;
    extracted.push({ label: 'Spouse 65+', line: 'header', value: 'Yes' });
  }

  // ── Form 1040 lines ──────────────────────────────────────────────
  assign(fields, extracted, 'wages', parsed.line1a_wages, 'Wages (W-2 box 1)');
  assign(fields, extracted, 'taxExemptInterest', parsed.line2a_taxExemptInterest, 'Tax-exempt interest');
  assign(fields, extracted, 'interestIncome', parsed.line2b_taxableInterest, 'Taxable interest');
  assign(fields, extracted, 'qualifiedDividends', parsed.line3a_qualifiedDividends, 'Qualified dividends');
  assign(fields, extracted, 'ordinaryDividends', parsed.line3b_ordinaryDividends, 'Ordinary dividends');
  assign(fields, extracted, 'iraDistributionConversion', parsed.line4b_iraTaxable, 'IRA distributions (taxable)');
  // Line 5b (taxable pensions & annuities): display-only.
  // The wizard's engine ignores the legacy `pensionSum` field and instead
  // sums five split inputs — Govt Pension (TP), Govt Pension (SP), Private
  // Pension (TP), Private Pension (SP), and Military Retirement — because
  // each split has different state-tax treatment (MA/NY/NJ/CO/UT/VT all
  // exclude govt and/or military differently from private). The 1040 alone
  // can't tell which dollars are govt vs private vs military; that split
  // lives on the underlying 1099-Rs. Auto-importing the line 5b lump into
  // any single split would actively mis-classify state tax. Surface it as
  // informational + a loud warning, and the advisor enters the split by
  // hand on the wizard's Income step.
  if (parsed.line5b_pensionsTaxable && parsed.line5b_pensionsTaxable.value > 0) {
    extracted.push({
      label: 'Pensions & annuities (taxable) — DETECTED, NOT IMPORTED (see warning)',
      line: `1040-${parsed.line5b_pensionsTaxable.sourceLineNumber}`,
      value: parsed.line5b_pensionsTaxable.value,
    });
  }
  assign(fields, extracted, 'grossSocialSecurity', parsed.line6a_socialSecurityGross, 'Social Security benefits (gross)');
  // Withholding: line 25d is the total of W-2 (25a), 1099 (25b) and other
  // (25c). Without 25d, add up whichever of the three were read.
  if (parsed.line25d_totalWithholding) {
    assign(fields, extracted, 'federalWithholding', parsed.line25d_totalWithholding, 'Federal withholding (all sources)');
  } else if (parsed.line25b_1099Withholding || parsed.line25c_otherWithholding) {
    const parts = [parsed.line25a_w2Withholding, parsed.line25b_1099Withholding, parsed.line25c_otherWithholding]
      .filter((f): f is ExtractedField<number> => f != null);
    const total = parts.reduce((sum, f) => sum + f.value, 0);
    fields.federalWithholding = total;
    extracted.push({
      label: 'Federal withholding (all sources)',
      line: `1040-${parts.map(f => f.sourceLineNumber).join('+')}`,
      value: total,
    });
  } else {
    assign(fields, extracted, 'federalWithholding', parsed.line25a_w2Withholding, 'Federal withholding (W-2)');
  }
  assign(fields, extracted, 'estimatedTaxPayments', parsed.line26_estimatedPayments, 'Estimated tax payments');

  // 1040 line 8 = Schedule 1 line 10 = SUM of business income (Sch C net),
  // rental + K-1 (Sch E), plus residual Schedule 1 items (unemployment,
  // gambling, hobby, etc.). The engine accepts schedCGrossReceipts/
  // schedCTotalExpenses, rentalIncome/rentalExpenses, and k1* fields
  // directly and adds them into totalIncome — so feeding line 8 raw on
  // top of those breakdowns double-counts the same dollars (an $815k
  // line-8 plus a $140k Sch C net + $55k rental + $620k K-1 = $815k of
  // phantom AGI). Subtract the breakdown nets and import only the
  // residual as otherIncome.
  const line8 = v(parsed.line8_otherIncomeSch1) ?? 0;
  const schedCNet =
    (v(parsed.schedCGrossReceipts) ?? 0) - (v(parsed.schedCTotalExpenses) ?? 0) +
    (v(parsed.spouseSchedCGrossReceipts) ?? 0) - (v(parsed.spouseSchedCTotalExpenses) ?? 0);
  const rentalNet = (v(parsed.rentalIncome) ?? 0) - (v(parsed.rentalExpenses) ?? 0);
  const k1Total =
    (v(parsed.k1OrdinaryBusinessIncome) ?? 0) +
    (v(parsed.k1GuaranteedPayments) ?? 0) +
    (v(parsed.k1RentalRealEstateIncome) ?? 0) +
    (v(parsed.k1OtherIncome) ?? 0);
  // A negative residual is real (a net operating loss on Schedule 1 line 8a,
  // say) and is kept, as v32's Schedule 1 other income takes one.
  const otherIncomeResidual = line8 - schedCNet - rentalNet - k1Total;
  if (parsed.line8_otherIncomeSch1 && otherIncomeResidual !== 0) {
    fields.otherIncome = otherIncomeResidual;
    extracted.push({
      label: 'Other income (Sch 1 residual)',
      line: '1040-8',
      value: otherIncomeResidual,
    });
  }

  // Line 10 (Adjustments to income, from Schedule 1 line 26). The 1040
  // carries only the lump total of Schedule 1 Part II; the engine takes each
  // category as its own field (SE health insurance and SE retirement also
  // feed QBI). When Schedule 1 itself is in the upload, its lines are
  // imported one by one below. When it isn't, the lump is display-only:
  // dropping it into `otherAdjustments` would double-count the moment the
  // advisor entered a category by hand, so the advisor enters the breakdown
  // on the Adjustments step.
  //
  // Line 15 (deductible SE tax) is never imported — the engine computes it
  // from Schedule C income.
  const sch1Adjustments: Array<[keyof TaxScenarioInput, ExtractedField<number> | undefined, string]> = [
    ['educatorExpenses', parsed.sch1Educator, 'Schedule 1: Educator expenses'],
    ['hsaDeduction', parsed.sch1HSA, 'Schedule 1: HSA deduction'],
    ['selfEmployedRetirement', parsed.sch1SERetirement, 'Schedule 1: SE retirement (SEP, SIMPLE, qualified)'],
    ['selfEmployedHealthInsurance', parsed.sch1SEHealthInsurance, 'Schedule 1: SE health insurance'],
    ['traditionalIRADeduction', parsed.sch1IRA, 'Schedule 1: IRA deduction'],
    ['studentLoanInterest', parsed.sch1StudentLoan, 'Schedule 1: Student loan interest'],
  ];
  // The wizard has no field of its own for these, so they share
  // `otherAdjustments`: reservist/artist/official expenses, armed-forces
  // moving, early-withdrawal penalty, alimony paid, Archer MSA, and the
  // line 24a-24z total.
  const sch1OtherParts = [
    parsed.sch1BusinessExpenses,
    parsed.sch1MovingExpenses,
    parsed.sch1EarlyWithdrawalPenalty,
    parsed.sch1Alimony,
    parsed.sch1ArcherMSA,
    parsed.sch1OtherAdjustments,
  ].filter((f): f is ExtractedField<number> => f != null);
  const sch1Lines = [
    ...sch1Adjustments.map(([, f]) => f),
    ...sch1OtherParts,
    parsed.sch1DeductibleSETax,
  ].filter((f): f is ExtractedField<number> => f != null);
  const usingSchedule1 = sch1Lines.length > 0;

  for (const [key, source, label] of sch1Adjustments) {
    assign(fields, extracted, key, source, label);
  }
  if (sch1OtherParts.length > 0) {
    const total = sch1OtherParts.reduce((sum, f) => sum + f.value, 0);
    fields.otherAdjustments = total;
    extracted.push({
      label: 'Schedule 1: Other adjustments',
      line: `Schedule 1-${sch1OtherParts.map(f => f.sourceLineNumber).join('+')}`,
      value: total,
    });
  }
  if (!usingSchedule1 && parsed.line10_adjustments && parsed.line10_adjustments.value > 0) {
    const v = parsed.line10_adjustments.value;
    extracted.push({
      label: 'Adjustments to income — DETECTED, NOT IMPORTED (see warning)',
      line: `1040-${parsed.line10_adjustments.sourceLineNumber}`,
      value: v,
    });
  }

  // Cap gains: prefer Schedule D split over 1040 line 7 lump.
  const usingSchedD = parsed.shortTermCapitalGains || parsed.longTermCapitalGains;
  if (usingSchedD) {
    assign(fields, extracted, 'shortTermCapitalGains', parsed.shortTermCapitalGains, 'Schedule D short-term cap gain');
    assign(fields, extracted, 'longTermCapitalGains', parsed.longTermCapitalGains, 'Schedule D long-term cap gain');
  } else if (parsed.line7_capitalGainOrLoss) {
    fields.longTermCapitalGains = parsed.line7_capitalGainOrLoss.value;
    extracted.push({
      label: 'Capital gain/(loss) — imported as long-term',
      line: '1040-7',
      value: parsed.line7_capitalGainOrLoss.value,
    });
  }

  // ── Schedule A ───────────────────────────────────────────────────
  assign(fields, extracted, 'medicalExpenses', parsed.schedAMedical, 'Schedule A: Medical expenses');
  assign(fields, extracted, 'saltStateLocal', parsed.schedASaltStateLocal, 'Schedule A: State/local income tax');
  assign(fields, extracted, 'saltRealEstate', parsed.schedASaltRealEstate, 'Schedule A: Real estate tax');
  assign(fields, extracted, 'saltPersonalProperty', parsed.schedASaltPersonalProperty, 'Schedule A: Personal property tax');
  // The wizard has one mortgage-interest field: 8a (on a Form 1098) and
  // 8b (not on one) both belong in it.
  if (parsed.schedAMortgageNotOn1098) {
    const total = (v(parsed.schedAMortgageInterest) ?? 0) + parsed.schedAMortgageNotOn1098.value;
    fields.homeMortgageInterest = total;
    extracted.push({
      label: 'Schedule A: Home mortgage interest (8a + 8b)',
      line: parsed.schedAMortgageInterest ? 'Schedule A-8a+8b' : 'Schedule A-8b',
      value: total,
    });
  } else {
    assign(fields, extracted, 'homeMortgageInterest', parsed.schedAMortgageInterest, 'Schedule A: Home mortgage interest');
  }
  assign(fields, extracted, 'mortgagePoints', parsed.schedAPoints, 'Schedule A: Points not on Form 1098');
  assign(fields, extracted, 'investmentInterest', parsed.schedAInvestmentInterest, 'Schedule A: Investment interest');
  assign(fields, extracted, 'cashGiftsPublicCharity', parsed.schedACashCharity, 'Schedule A: Cash charity');
  assign(fields, extracted, 'nonCashPropertyPublicCharity', parsed.schedANonCashCharity, 'Schedule A: Non-cash charity');
  assign(fields, extracted, 'otherItemizedDeductions', parsed.schedAOther, 'Schedule A: Other itemized deductions');

  // ── Schedule C ───────────────────────────────────────────────────
  assign(fields, extracted, 'schedCGrossReceipts', parsed.schedCGrossReceipts, 'Schedule C: Gross receipts');
  assign(fields, extracted, 'schedCTotalExpenses', parsed.schedCTotalExpenses, 'Schedule C: Total expenses');
  assign(fields, extracted, 'spouseSchedCGrossReceipts', parsed.spouseSchedCGrossReceipts, 'Schedule C (spouse): Gross receipts');
  assign(fields, extracted, 'spouseSchedCTotalExpenses', parsed.spouseSchedCTotalExpenses, 'Schedule C (spouse): Total expenses');

  // ── Schedule SE: each business owner's own W-2 Social Security wages ──
  // Self-employment tax is capped per person, and line 1a is both spouses'
  // W-2s combined. Each owner's Schedule SE line 8a says what they earned
  // on their own W-2s, so that goes straight into their W-2 SS Wages box.
  let wageSplitTotal: number | null = null;
  const tpSeWages = v(parsed.schedSETaxpayerW2SsWages);
  const spSeWages = v(parsed.schedSESpouseW2SsWages);
  if (tpSeWages) {
    fields.w2SocialSecurityWages = tpSeWages;
    extracted.push({ label: 'Schedule SE: W-2 Social Security wages', line: 'Schedule SE-8a', value: tpSeWages });
  }
  if (spSeWages) {
    fields.spouseW2SocialSecurityWages = spSeWages;
    extracted.push({ label: 'Schedule SE (spouse): W-2 Social Security wages', line: 'Schedule SE (spouse)-8a', value: spSeWages });
  }
  if (v(parsed.filingStatus) === 'MFJ' && (fields.wages ?? 0) > 0) {
    // The taxpayer's Schedule SE shows no W-2 wages of their own, so every
    // dollar on line 1a is the spouse's: move it to Wages (Spouse). (Not when
    // the spouse's Schedule SE also shows none — then line 1a has no owner.)
    const tpNoW2 = parsed.schedSETaxpayerFound && !tpSeWages;
    const spNoW2 = parsed.schedSESpouseFound && !spSeWages;
    if (tpNoW2 && !spNoW2) {
      fields.spouseWages = fields.wages;
      fields.wages = 0;
      extracted.push({ label: 'Wages moved to Wages (Spouse): taxpayer has no W-2 per Schedule SE', line: '1040-1a', value: fields.spouseWages });
    }
    // Someone has a business but no Schedule SE was read for them, so their
    // own W-2 wages can't be told apart from their spouse's. The import
    // screen asks for the split right there (Import1040Dialog).
    const net = (g?: ExtractedField<number>, e?: ExtractedField<number>) => (v(g) ?? 0) - (v(e) ?? 0);
    const tpUnknown = net(parsed.schedCGrossReceipts, parsed.schedCTotalExpenses) >= 400 && !parsed.schedSETaxpayerFound;
    const spUnknown = net(parsed.spouseSchedCGrossReceipts, parsed.spouseSchedCTotalExpenses) >= 400 && !parsed.schedSESpouseFound;
    if (tpUnknown || spUnknown) wageSplitTotal = fields.wages ?? 0;
  }

  // ── Schedule E ───────────────────────────────────────────────────
  if (parsed.rentalIncome) {
    fields.rentalIncome = parsed.rentalIncome.value;
    fields.rentalActive = true;
    extracted.push({
      label: 'Schedule E: Rental income (all properties)',
      line: `Schedule E-${parsed.rentalIncome.sourceLineNumber}`,
      value: parsed.rentalIncome.value,
    });
  }
  if (parsed.rentalExpenses) {
    fields.rentalTotalExpenses = parsed.rentalExpenses.value;
    extracted.push({
      label: 'Schedule E: Rental total expenses (all properties)',
      line: `Schedule E-${parsed.rentalExpenses.sourceLineNumber}`,
      value: parsed.rentalExpenses.value,
    });
  }
  assign(fields, extracted, 'k1OrdinaryBusinessIncome', parsed.k1OrdinaryBusinessIncome, 'Schedule E: K-1 ordinary business income');
  assign(fields, extracted, 'k1GuaranteedPayments', parsed.k1GuaranteedPayments, 'Schedule E: K-1 guaranteed payments');
  assign(fields, extracted, 'k1RentalRealEstateIncome', parsed.k1RentalRealEstateIncome, 'Schedule E: K-1 rental real estate');
  assign(fields, extracted, 'k1OtherIncome', parsed.k1OtherIncome, 'Schedule E: K-1 other income');

  // ── QBI / AMT ────────────────────────────────────────────────────
  assign(fields, extracted, 'qbiW2Wages', parsed.qbiW2Wages, 'Form 8995-A: QBI W-2 wages');
  assign(fields, extracted, 'qbiUBIA', parsed.qbiUBIA, 'Form 8995-A: QBI UBIA');
  if (parsed.qbiSSTB?.value) {
    fields.qbiSSTB = true;
    extracted.push({ label: 'Form 8995-A: SSTB classification', line: 'Form 8995-A-I', value: 'Yes' });
  }
  // AMT adjustments. Two signals, in order of preference:
  //   1. AMTI (line 4) - regular base (line 1b) — most reliable on real
  //      IRS-generated 6251 forms, since line 2 itself is a category header.
  //   2. Direct line-2 lookup (parsed.amtAdjustment) — synthetic test
  //      fixtures and older form layouts where line 2 carries a value.
  // Whichever path produces a value, fields.amtAdjustment AND the extracted
  // row are written in lockstep through a single push. ASCII-only label
  // (no em-dash) so the dialog renders consistently across every browser/
  // font fallback.
  const amti = v(parsed.amti);
  const amtRegularBase = v(parsed.amtRegularBase);
  let amtValue: number | undefined;
  let amtLine = 'Form 6251-2';
  let amtLabel = 'Form 6251: AMT adjustments';
  // An itemizer's line 2a is their Schedule A taxes, and the engine adds
  // SALT back for AMT on its own (engine.ts saltAddback). Leaving 2a in
  // counted it twice, so it comes out here. A non-itemizer's 2a is the
  // standard deduction, which the engine does not add, so it stays in.
  const itemized = [parsed.schedASaltStateLocal, parsed.schedASaltRealEstate, parsed.schedASaltPersonalProperty,
    parsed.schedAMortgageInterest, parsed.schedAInvestmentInterest, parsed.schedACashCharity,
    parsed.schedANonCashCharity, parsed.schedAMedical].some(f => f != null);
  const line2a = v(parsed.amtLine2a);
  // Can't tell how much of the total is SALT, so import none of it rather
  // than count SALT twice.
  const amt2aUnread = amti != null && amtRegularBase != null && itemized && line2a == null;
  if (amti != null && amtRegularBase != null) {
    if (!amt2aUnread) {
      amtValue = Math.max(0, amti - amtRegularBase - (itemized ? (line2a ?? 0) : 0));
      amtLine = 'Form 6251-4';
      amtLabel = itemized
        ? 'Form 6251: AMT adjustments (AMTI - line 1b - line 2a taxes)'
        : 'Form 6251: AMT adjustments (AMTI - line 1b)';
    }
  } else if (parsed.amtAdjustment != null) {
    amtValue = parsed.amtAdjustment.value;
    amtLine = `${parsed.amtAdjustment.sourceForm}-${parsed.amtAdjustment.sourceLineNumber}`;
  }
  if (amtValue !== undefined) {
    fields.amtAdjustment = amtValue;
    extracted.push({ label: amtLabel, line: amtLine, value: amtValue });
  }

  // ── Warnings: compose flat strings for the review dialog ─────────
  const warnings = parsed.warnings.map(w => w.message);
  if (amt2aUnread) warnings.push('Form 6251 adjustments were not imported: line 2a (taxes) could not be read.');

  // Add static caveats.
  if (extracted.filter(e => e.line !== 'header').length === 0) {
    warnings.push('Found a 1040 header but no line items parsed. The PDF text layout may be non-standard.');
  }
  if (!parsed.shortTermCapitalGains && !parsed.longTermCapitalGains && parsed.line7_capitalGainOrLoss) {
    warnings.push('Line 7 capital gain is imported as long-term. Add Schedule D to the upload (or adjust on the Income step) for short-term/long-term split.');
  }
  if (!parsed.addressState) {
    warnings.push('Could not auto-detect state from address. Please select on the Filing step.');
  }
  // The 59½ flags only feed New York's pension exclusion, and the return
  // doesn't carry them.
  if (fields.state === 'New York') {
    warnings.push('NY 59½ flags are not auto-imported. Set NY TP 59½+ / NY SP 59½+ on the Filing step.');
  }
  if (usingSchedule1 && parsed.line10_adjustments) {
    // Every Part II line read (line 15 included) should add back up to the
    // 1040's line 10. If not, a line was missed or misread.
    const readTotal = sch1Lines.reduce((sum, f) => sum + f.value, 0);
    const line10 = parsed.line10_adjustments.value;
    if (Math.abs(readTotal - line10) > 1) {
      warnings.push(
        `Schedule 1 adjustments imported line by line add up to $${readTotal.toLocaleString()}, but Form 1040 line 10 shows $${line10.toLocaleString()}. Check the Above-the-Line Adjustments section on the Deductions & Adjustments step.`,
      );
    }
  } else if (parsed.line10_adjustments && parsed.line10_adjustments.value > 0) {
    const v = parsed.line10_adjustments.value;
    warnings.push(
      `Schedule 1 adjustments ($${v.toLocaleString()}) detected — not auto-imported. Enter on the Deductions & Adjustments step, Above-the-Line Adjustments section. If any is SE Health Insurance (line 17) or SE Retirement (line 16), use those specific fields — they affect QBI separately. Skip line 15 (deductible SE tax) — the engine computes it from Schedule C.`,
    );
  }
  if (parsed.line5b_pensionsTaxable && parsed.line5b_pensionsTaxable.value > 0) {
    const v = parsed.line5b_pensionsTaxable.value;
    warnings.push(
      `Taxable pensions & annuities ($${v.toLocaleString()}) detected — not auto-imported. The 1040 doesn't carry the govt vs private vs military breakdown (that lives on the 1099-Rs), and the split changes state tax in MA, NY, NJ, CO, UT, and VT. Enter the breakdown on the Income step under "Pensions & Military Retirement" — Govt Pension, Private Pension, and Military Retirement boxes.`,
    );
  }

  return {
    fields,
    extracted,
    warnings,
    wageSplitTotal,
    structuredWarnings: parsed.warnings,
  };
}

/**
 * Format the wizard's free-text "Client Name" field from parsed parts.
 * - Single / HOH / MFS → "First Last"
 * - MFJ same last name → "First & SpouseFirst Last"
 * - MFJ different last names → "First Last & SpouseFirst SpouseLast"
 */
function formatClientName(
  isJoint: boolean,
  taxpayerFirst: string | undefined,
  taxpayerLast: string | undefined,
  spouseFirst: string | undefined,
  spouseLast: string | undefined,
): string | null {
  if (!taxpayerFirst || !taxpayerLast) return null;
  if (!isJoint || !spouseFirst || !spouseLast) {
    return `${taxpayerFirst} ${taxpayerLast}`;
  }
  if (taxpayerLast === spouseLast) {
    return `${taxpayerFirst} & ${spouseFirst} ${taxpayerLast}`;
  }
  return `${taxpayerFirst} ${taxpayerLast} & ${spouseFirst} ${spouseLast}`;
}
